/*******************************************************************
* PAGE        : TicketRateList
* REMARKS     :
* CREATED ON  : 27-08-2026
* AUTHOR      : Bamroliya.Paras
* MODIFY      : Bamroliya.Paras
* MODIFY ON   : 27-08-2026
********************************************************************/

import { TablePagination, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import addsquare from "../../assets/add-square.svg";
import deleteicon from "../../assets/delete.svg";
import editsquare from "../../assets/edit-square.svg";
import filtericon from "../../assets/filter-icon.svg";
import DialogComponent from '../../components/DialogComponent/DialogComponent';
import LoaderComponent from '../../components/LoaderComponent/LoaderComponent';
import NotificationComponent from '../../components/NotificationComponent/NotificationComponent';
import { DeleteTicketClassificationRate, GetTicketClassificationRates } from '../../services/apiController';
import AddEditTicketRate from './AddEditTicketRate';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';

// #region Component
const TicketRateList = () => {

    // #region States
    const navigate = useNavigate();

    const { state } = useLocation();

    const eventMasterId = state?.eventMasterId;

    const [loading, setLoading] = useState(false);

    const [openPopup, setOpenPopup] = useState(false);
    const [openPopupAction, setOpenPopupAction] = useState(0);
    const [selectedData, setSelectedData] = useState({});

    const [rateList, setRateList] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(10); // Default rows per page
    const [currentPage, setCurrentPage] = useState(1); // Default current page
    const [maxRows, setMaxRows] = useState(0); // Total number of rows
    const [gotoPage, setGotoPage] = useState(1); // Page input for jump
    const [totalPages, setTotalPages] = useState(1); // Total pages calculation
    const [searchQuery, setSearchQuery] = useState(''); // For search query
    const [sortOrder, setSortOrder] = useState({ column: '', order: 'asc' }); // For sorting state

    const [columnCount, setColumnCount] = useState(1);

    // State to manage snackbar visibility and messages
    const [snackbarStates, setSnackbarStates] = useState({ isSnackbarOpen: false, snackbarMessage: '' });
    const handleCloseSnackbar = () => {
        setSnackbarStates({ isSnackbarOpen: false, snackbarMessage: '', snackbarSeverity: '' });
    };
    // #endregion

    useEffect(() => {
        const thCount = document.querySelectorAll('thead th').length;
        setColumnCount(thCount || 1);
    }, []);

    useEffect(() => {
        wsGetTicketClassificationRates();
    }, [rowsPerPage, currentPage]); // Re-run the effect when rowsPerPage or currentPage changes

    // #region API Methods
    const wsGetTicketClassificationRates = async () => {
        setLoading(true);
        const ticketEnquiryData = {
            ticketMasterId: eventMasterId
        }

        GetTicketClassificationRates(ticketEnquiryData)
            .then((response) => {
                if (response) {
                    setRateList(response?.ticketClassificationRates);
                    setMaxRows(response?.ticketClassificationRates?.length); // Set total rows from response
                    setTotalPages(Math.ceil(response?.ticketClassificationRates?.length / rowsPerPage)); // Calculate total pages
                } else {
                    // Handle error scenario
                }
                setLoading(false);
            }).catch((error) => {
                setLoading(false);
                // Handle API error
            });
    }

    const wsDeleteTicketClassificationRate = async (ticketClassificationId) => {
        setLoading(true);
        const params = {
            ticketMasterId: eventMasterId,
            ticketClassificationId: ticketClassificationId
        }

        DeleteTicketClassificationRate(params).then((response) => {
            if (response.status) {
                setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: response?.message || "Rate delete successfully", snackbarSeverity: "success" });
            } else {
                setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: response.message, snackbarSeverity: 'error' });
            }
            handleClose();
            setLoading(false);
        }).catch((error) => {
            setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: "Something went wrong please try again", snackbarSeverity: 'error' });
            handleClose();
            setLoading(false);
        });
    }
    //#endregion

    // #region Methods
    // Handle page change
    const handleChangePage = (event, newPage) => {
        setCurrentPage(newPage + 1); // MUI TablePagination uses 0-based index
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(1); // Reset to page 1 when rows per page changes
    };

    // Handle page input change
    const handleGotoPageChange = (event) => {
        setGotoPage(event.target.value);
    };

    // Handle the submit (Enter key press) for "Go to page"
    const handleSubmitGotoPage = (event) => {
        if (event.key === 'Enter') {
            const pageNumber = Math.max(1, Math.min(totalPages, parseInt(gotoPage, 10))); // Ensure valid page
            setCurrentPage(pageNumber);
        }
    };

    // Handle search query change
    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    // Filtered and sorted rateList
    const SEARCH_FIELDS = [
        'TicketType',
        'TicketRate',
        'MinimumTickets',
        'MaxTicket',
        'ApplicableStartDateTime',
        'ApplicableEndDateTime',
        'IsActive'
    ];

    const filteredRates = rateList?.filter((rate) =>
        SEARCH_FIELDS.some((field) =>
            rate[field]
                ?.toString()
                .toLowerCase()
                .includes(searchQuery.toLowerCase())
        )
    );

    // Sorting function
    const handleSort = (column) => {
        const newOrder = sortOrder.order === 'asc' ? 'desc' : 'asc';
        setSortOrder({
            column: column,
            order: newOrder,
        });
    };

    const sortedRates = filteredRates.sort((a, b) => {
        if (!sortOrder.column) return 0;

        const aValue = a[sortOrder.column];
        const bValue = b[sortOrder.column];

        if (aValue < bValue) return sortOrder.order === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder.order === 'asc' ? 1 : -1;
        return 0;
    });

    const handleDeleteRateCode = () => {
        wsDeleteTicketClassificationRate(selectedData?.TicketClassificationId || 0)
    }

    const handleClose = () => {
        setOpenPopup(false);
        setOpenPopupAction(0);
        wsGetTicketClassificationRates();
    }

    // #endregion

    //#region UI
    return (
        loading ? <LoaderComponent />
            : <React.Fragment>
                <NotificationComponent isOpen={snackbarStates.isSnackbarOpen} message={snackbarStates.snackbarMessage} onClose={handleCloseSnackbar} severity={snackbarStates.snackbarSeverity} />

                <div style={{ padding: '10px 20px 50px', overflowX: 'auto' }}>
                    <div className='table-heading-search' style={{ marginBottom: '10px', }}>
                        <h2 className='dialog-heading' style={{ marginBlockEnd: '0px' }}>Ticket Rate</h2>

                        {/* Search Input */}
                        <div className='table-heading-search' style={{ alignItems: 'center', gap: '16px' }}>
                            Search: <input type="text" value={searchQuery} onChange={handleSearchChange} style={{ padding: '5px', width: '200px', border: '1px solid #ccc', borderRadius: '4px', }} />
                            <ButtonComponent style={{ padding: "8px 16px", textTransform: 'none' }} onClick={() => { setOpenPopup(true); setOpenPopupAction(1); setSelectedData({}); }}>Add New Ticket Rate</ButtonComponent>
                            {/* <img src={addsquare} className="dialog-close-icon" onClick={() => { setOpenPopup(true); setOpenPopupAction(1); setSelectedData({}); }} /> */}
                        </div>

                    </div>

                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th className='tableHeaderStyle' onClick={() => handleSort('TicketType')}>Ticket Type</th>
                                <th className='tableHeaderStyle' onClick={() => handleSort('TicketRate')} style={{ textAlign: 'right', width: '150px' }}>Ticket Rate</th>
                                <th className='tableHeaderStyle' onClick={() => handleSort('MinimumTickets')} style={{ textAlign: 'right', width: '150px' }}>Minimum Ticket</th>
                                <th className='tableHeaderStyle' onClick={() => handleSort('MaxTicket')} style={{ textAlign: 'right', width: '150px' }}>Maximum Ticket</th>
                                <th className='tableHeaderStyle' onClick={() => handleSort('ApplicableStartDateTime')} style={{ textAlign: 'center', width: '250px' }}>Applicable At</th>
                                <th className='tableHeaderStyle' onClick={() => handleSort('ApplicableEndDateTime')} style={{ textAlign: 'center', width: '250px' }}>Expiry At</th>
                                <th className='tableHeaderStyle' onClick={() => handleSort('IsActive')} style={{ textAlign: 'center', width: '150px' }}>Is Active ?</th>
                                <th className='tableHeaderStyle' style={{ textAlign: 'center' }}><img src={filtericon} alt='filtericon' /></th>
                            </tr>
                        </thead>
                        <tbody>
                            {!sortedRates.length
                                ? <tr>
                                    <td colSpan={columnCount} style={{ textAlign: 'center', padding: '10px', fontSize: '16px' }}>
                                        No Record Found ☹️
                                    </td>
                                </tr>
                                : sortedRates.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                                    .map((item, index) => (
                                        <tr key={index} style={{ ...tableRowStyle, backgroundColor: index % 2 === 1 ? '#ececec' : '#FFFFFF' }}>
                                            <td className='tableCellStyle'>{item.TicketType}</td>
                                            <td className='tableCellStyle' style={{ textAlign: 'right', width: '150px' }}>{item.TicketRate}</td>
                                            <td className="tableCellStyle" style={{ textAlign: 'right', width: '150px' }}>{item.MinimumTickets}</td>
                                            <td className="tableCellStyle" style={{ textAlign: 'right', width: '150px' }}>{item.MaxTicket}</td>
                                            <td className="tableCellStyle" style={{ textAlign: 'center', width: '250px' }}>{item.ApplicableStartDateTime}</td>
                                            <td className="tableCellStyle" style={{ textAlign: 'center', width: '250px' }}>{item.ApplicableEndDateTime}</td>
                                            <td className="tableCellStyle" style={{ textAlign: 'center', width: '150px' }}>
                                                <span
                                                    style={{
                                                        backgroundColor: item.IsActive ? '#d1fae5' : '#fee2e2',
                                                        color: item.IsActive ? '#065f46' : '#991b1b',
                                                        padding: '4px 16px',
                                                        borderRadius: '16px',
                                                        fontSize: '14px',
                                                        fontWeight: '600',
                                                    }}
                                                >
                                                    {item.IsActive ? 'True' : 'False'}
                                                </span>
                                            </td>
                                            <td className='tableCellStyle' style={{ textAlign: 'center', width: '100px' }}>
                                                {/* <Tooltip title="Edit Rate" arrow>
                                                    <img src={editsquare} alt='editsquare' style={{ cursor: 'pointer', paddingRight: '4px' }} onClick={() => { setOpenPopup(true); setOpenPopupAction(1); setSelectedData(item); }} />
                                                </Tooltip> */}
                                                <Tooltip title="Delete Rate" arrow>
                                                    <img src={deleteicon} alt='deleteicon' style={{ cursor: 'pointer', paddingLeft: '4px' }} onClick={() => { setOpenPopup(true); setOpenPopupAction(2); setSelectedData(item); }} />
                                                </Tooltip>
                                            </td>
                                        </tr>
                                    ))}
                        </tbody>
                    </table>

                    {/* Pagination Controls */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '10px' }}>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100, 200, 500, 1000]}
                            component="div"
                            count={maxRows}
                            rowsPerPage={rowsPerPage}
                            page={currentPage - 1} // MUI TablePagination uses 0-based index
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            id="page-box"
                        />
                        <div style={{ fontSize: '13px', marginLeft: '10px' }}>
                            Page: <input style={{ width: '50px', border: '1px solid #ccc', borderRadius: '5px', padding: '4px' }} type="number" value={gotoPage} disabled={totalPages <= 1} onChange={handleGotoPageChange} onKeyDown={handleSubmitGotoPage} /> Of {totalPages}
                        </div>
                    </div>
                </div>

                {/* Popup */}
                {openPopup && openPopupAction === 1 &&
                    <AddEditTicketRate
                        openPopup={openPopup}
                        loading={loading}
                        eventMasterId={eventMasterId}
                        selectedData={selectedData}
                        handleClose={handleClose} />
                }

                {openPopup && openPopupAction === 2 &&
                    <DialogComponent
                        isOpen={openPopup}
                        Title="Delete Rate"
                        ActionText={"Delete"}
                        IsLoading={loading}
                        onSave={handleDeleteRateCode}
                        onClose={handleClose}
                    >
                        <label>Are you sure you want to delete this Rate ?</label>
                    </DialogComponent>
                }
            </React.Fragment>
    );
    //#endregion
};
// #endregion

const tableRowStyle = {
    borderBottom: '1px solid #eee',
};


export default TicketRateList;